/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import familyLawConsultingCheckinStyle from "./familyLawConsultingCheckinStyle";
import * as loginActions from "../../../../store/login/actions";
import { useLocation, useNavigate } from "react-router";
import { getLpByLocation } from "../../../../services/landingPagesList";
import Typography from "@mui/material/Typography";
import { FormProvider } from "../../../../provider/Form/FormProvider";
import FamilyLawCheckinForm from "../Alimony/FamilyLawCheckinForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource, queryKeys } from "../../../../services/reactQuery/reactQueryService";
import { Box } from "@mui/material";
import type { PartnerIri } from "../../../../constants/partnerIris";
import { getPartnerNameByIri, PARTNER_IRIS } from "../../../../constants/partnerIris";
import ValidatorSelectField from "../../../FormFields/ValidatorSelectField";
import { requiredValidator } from "../../../../services/validationRules";
import MenuItem from "@mui/material/MenuItem";

type FamilyLawConsultingCheckinProps = {
  idPrefix: string;
  color?: "default" | "grey";
  checkinHeading?: string;
  target?: string;
  partner: PartnerIri;
};

export default function FamilyLawConsultingCheckin({
  color = "default",
  target = "/servicewelt",
  idPrefix,
  checkinHeading = "Jetzt professionelle Erstberatung vereinbaren",
  partner,
}: FamilyLawConsultingCheckinProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const initialValues = {
    telephone: "",
    email: "",
    gender: "",
    familyName: "",
    givenName: "",
    product: "",
  };
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });

  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsLoading(true);

    const { from } = location.state || { from: { pathname: target } };
    const emailCheck = await loginActions.checkEmail(values.email.toLowerCase());
    if (emailCheck.hasError) {
      setIsLoading(false);
      setEmailErrorText(emailCheck.errorText);
      return;
    }

    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };
    const partnerName = getPartnerNameByIri(partner);
    const data = {
      email: values.email.toLowerCase(),
      gclid: localStorage.getItem("gclid"),
      telephone: values.telephone,
      person: {
        gender: values.gender,
        givenName: values.givenName,
        familyName: values.familyName,
      },
      prelead: {
        initialProduct: "familyLawCase",
        landingPage: landingPage.keyword,
        partner: partnerName,
      },
    };

    const getTargetUri = (selectedProduct: string) => {
      switch (selectedProduct) {
        case "alimony":
          return "/alimonies";
        case "death":
          return "/deaths";
        case "other":
        default:
          return "/family_law_cases";
      }
    };

    await loginActions.register(data);
    await createMutation.mutateAsync({
      uri: getTargetUri(values.product),
      data: {
        acquisitionPartner: partner,
        affiliateCode: localStorage.getItem("gclid"),
        landingPage: landingPage.keyword,
      },
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });
    navigate(from.pathname);
  };

  return (
    <Paper elevation={4} sx={[familyLawConsultingCheckinStyle.formPaper, familyLawConsultingCheckinStyle[color]]}>
      <Box id={idPrefix + "-checkin"} sx={familyLawConsultingCheckinStyle.scrollToAnchor} />
      <Typography variant={"h4"} sx={familyLawConsultingCheckinStyle.heading}>
        {checkinHeading}
      </Typography>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <FamilyLawCheckinForm
          isLoading={isLoading}
          emailErrorText={emailErrorText}
          setEmailErrorText={setEmailErrorText}
          additionalFormFields={
            partner === PARTNER_IRIS.DEURAG && (
              <ValidatorSelectField label="Ihr Anliegen" name="product" validators={[requiredValidator("Bitte geben Sie Ihr Anliegen an.")]}>
                <MenuItem value={"alimony"}>Unterhalt</MenuItem>
                <MenuItem value={"death"}>Erbe/Todesfall</MenuItem>
                <MenuItem value={"other"}>Anderes Thema</MenuItem>
              </ValidatorSelectField>
            )
          }
        />
      </FormProvider>
    </Paper>
  );
}
